import * as React from "react";
import { useMemo, useState } from "react";
import { useLocalizeSafe } from "@/modules/shared/context/localize/LocalizeContext";

type FallbackPageProps = {
  /** Human-friendly title shown to the user */
  title?: string;
  /** Short description or hint for the user */
  description?: string;
  /** Error object if available (we'll show its message/stack) */
  error?: unknown;
  /** Callback to retry the action */
  onRetry?: () => void;
  /** Optional "Report issue" handler; if not provided, the button is hidden */
  onReportIssue?: () => void;
  /** Optional CTA to go home */
  homeHref?: string;
  /** App/brand name used in footer or meta text */
  appName?: string;
};

function getErrorMessage(err: unknown): string {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message || "Unknown error";
  try {
    return JSON.stringify(err);
  } catch {
    return "Unknown error";
  }
}

function getErrorStack(err: unknown): string | undefined {
  if (err instanceof Error && err.stack) return err.stack;
  return undefined;
}

export function ErrorFallback({
  title,
  description,
  error,
  onRetry,
  onReportIssue,
  homeHref = "/",
  appName = "EasyTacho",
}: FallbackPageProps) {
  const { tSafe } = useLocalizeSafe()
  const t = (key: string, fallback: string) => tSafe(`shared.errorFallback.${key}`, fallback)

  const resolvedTitle = title ?? t('title', 'Something went wrong')
  const resolvedDescription = description ?? t('description', 'Please try again. If the problem persists, contact support.')

  // Never expose error message/stack/diagnostics in production builds.
  const showDetails = process.env.NODE_ENV !== "production";

  const [expanded, setExpanded] = useState(false);
  const message = useMemo(() => getErrorMessage(error), [error]);
  const stack = useMemo(() => getErrorStack(error), [error]);
  const [copied, setCopied] = useState(false);

  // Build a compact error payload for copy/report
  const errorPayload = useMemo(() => {
    const payload = {
      app: appName,
      message,
      stack,
      userAgent: typeof window !== "undefined" ? window.navigator.userAgent : "server",
      time: new Date().toISOString(),
      url: typeof window !== "undefined" ? window.location.href : "server",
    };
    return JSON.stringify(payload, null, 2);
  }, [appName, message, stack]);

  async function copyError() {
    try {
      await navigator.clipboard.writeText(errorPayload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // no-op
    }
  }

  return (
    <main
      role="main"
      className="tw-min-h-screen tw-bg-gray-50 tw-text-gray-900 tw-flex tw-items-center tw-justify-center tw-p-4"
    >
      <section
        aria-labelledby="fallback-title"
        className="tw-w-full tw-max-w-2xl tw-bg-white tw-rounded-2xl tw-shadow-lg tw-border tw-border-gray-200 tw-p-6 md:tw-p-8"
      >
        {/* Header */}
        <div className="tw-flex tw-items-start tw-gap-4">
          <div
            aria-hidden="true"
            className="tw-shrink-0 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-w-12 tw-h-12 tw-bg-red-100 tw-text-red-700 tw-font-bold"
          >
            !
          </div>
          <div className="tw-flex-1">
            <h1 id="fallback-title" className="tw-text-2xl tw-font-semibold tw-tracking-tight">
              {resolvedTitle}
            </h1>
            <p className="tw-mt-1 tw-text-sm tw-text-gray-600">{resolvedDescription}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="tw-mt-6 tw-flex tw-flex-wrap tw-gap-3">
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-gray-900 tw-text-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium hover:tw-bg-black focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-900 focus:tw-ring-offset-2"
            >
              {t('tryAgain', 'Try again')}
            </button>
          )}
          {onReportIssue && (
            <button
              type="button"
              onClick={onReportIssue}
              className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-white tw-text-gray-900 tw-border tw-border-gray-300 tw-px-4 tw-py-2 tw-text-sm tw-font-medium hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-900 focus:tw-ring-offset-2"
            >
              {t('reportIssue', 'Report issue')}
            </button>
          )}
          {homeHref && (
            <a
              href={homeHref}
              className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-white tw-text-gray-900 tw-border tw-border-gray-300 tw-px-4 tw-py-2 tw-text-sm tw-font-medium hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-900 focus:tw-ring-offset-2"
            >
              {t('goHome', 'Go home')}
            </a>
          )}
          {showDetails && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls="error-details"
              className="tw-ml-auto tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-white tw-text-gray-900 tw-border tw-border-gray-300 tw-px-4 tw-py-2 tw-text-sm tw-font-medium hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-900 focus:tw-ring-offset-2"
            >
              {expanded ? t('hideDetails', 'Hide details') : t('showDetails', 'Show details')}
            </button>
          )}
        </div>

        {/* Error details */}
        {showDetails && (
        <div
          id="error-details"
          className={`tw-mt-6 tw-grid tw-transition-[grid-template-rows] tw-duration-300 tw-ease-out ${
            expanded ? "tw-grid-rows-[1fr]" : "tw-grid-rows-[0fr]"
          }`}
        >
          <div className="tw-overflow-hidden">
            <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4">
              <p className="tw-text-sm tw-font-semibold tw-text-gray-800">{t('error', 'Error')}</p>
              <pre className="tw-mt-2 tw-text-xs tw-text-gray-700 tw-whitespace-pre-wrap tw-break-words">
                {message}
              </pre>
              {stack && (
                <>
                  <p className="tw-mt-4 tw-text-sm tw-font-semibold tw-text-gray-800">{t('stackTrace', 'Stack trace')}</p>
                  <pre className="tw-mt-2 tw-text-[11px] tw-text-gray-700 tw-whitespace-pre tw-overflow-x-auto">
                    {stack}
                  </pre>
                </>
              )}
              <div className="tw-mt-4 tw-flex tw-gap-3">
                <button
                  type="button"
                  onClick={copyError}
                  className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-gray-900 tw-text-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium hover:tw-bg-black focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-900 focus:tw-ring-offset-2"
                >
                  {copied ? t('copied', 'Copied!') : t('copyError', 'Copy error')}
                </button>
                <details className="tw-ml-auto tw-text-xs tw-text-gray-500">
                  <summary className="tw-cursor-pointer tw-select-none">{t('diagnosticJson', 'Diagnostic JSON')}</summary>
                  <pre className="tw-mt-2 tw-whitespace-pre-wrap">{errorPayload}</pre>
                </details>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Footer */}
        <footer className="tw-mt-8 tw-text-xs tw-text-gray-500 tw-text-center">
          © {new Date().getFullYear()} {appName}. {t('allRightsReserved', 'All rights reserved.')}
        </footer>
      </section>
    </main>
  );
}
